import type { ReactNode } from 'react';
import { Search, UserPlus, type LucideIcon } from 'lucide-react';

import { ApplicationQueueToggle } from '@/components/applications/ApplicationQueueToggle';
import { ClearListFiltersButton } from '@/components/shared/ClearListFiltersButton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { activeListFilterClass } from '@/lib/listFilterUi';
import { cn } from '@/lib/utils';
import type { ApplicationQueueView } from '@/types';

type SelectOption = { value: string; label: string };

type QueueProps = {
  value: ApplicationQueueView;
  onChange: (view: ApplicationQueueView) => void;
  activeLabel: string;
  archiveLabel: string;
  activeCount?: number | null;
  activeCountLoading?: boolean;
  activeCountHasMore?: boolean;
  archivedCount?: number | null;
  archivedCountHasMore?: boolean;
};

type SearchProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
};

type SelectFilterProps = {
  value: string;
  onChange: (value: string) => void;
  active: boolean;
  placeholder?: string;
  options: SelectOption[];
};

type PrimaryActionProps = {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  icon?: LucideIcon;
};

type Props = {
  search: SearchProps;
  status: SelectFilterProps;
  queue?: QueueProps;
  position?: SelectFilterProps;
  team?: SelectFilterProps;
  source?: SelectFilterProps;
  starred?: SelectFilterProps;
  /** @deprecated use primaryAction */
  addCandidate?: PrimaryActionProps;
  primaryAction?: PrimaryActionProps;
  showClearFilters?: boolean;
  onClearFilters?: () => void;
  clearFiltersLabel?: string;
};

function FilterCell({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'min-w-0 [&_button]:h-9 [&_button]:w-full [&_[role=combobox]]:h-9 [&_[role=combobox]]:w-full',
        className,
      )}
    >
      {children}
    </div>
  );
}

function StatusSelect({ filter }: { filter: SelectFilterProps }) {
  return (
    <Select value={filter.value} onValueChange={filter.onChange}>
      <SelectTrigger className={activeListFilterClass(filter.active, 'h-9 w-full md:w-[180px]')}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {filter.options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function GenericSelect({ filter, widthClass }: { filter: SelectFilterProps; widthClass: string }) {
  return (
    <Select value={filter.value} onValueChange={filter.onChange}>
      <SelectTrigger className={activeListFilterClass(filter.active, cn('h-9 w-full', widthClass))}>
        <SelectValue placeholder={filter.placeholder} />
      </SelectTrigger>
      <SelectContent>
        {filter.options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * List toolbar.
 *
 * Mobile layout (< md):
 *   Row 1: [search input ············] [Add candidate]
 *   Row 2: [queue toggle — full width] (optional)
 *   Row 3: 2-col filter grid (status, position, source, team, clear)
 *
 * Desktop layout (≥ md):
 *   Single wrapping row: queue | search | status | position | source | team | clear | primary action.
 *   When space is tight the primary action wraps with the filter selects instead of staying
 *   pinned to the far right.
 */
export function ApplicationsListFiltersToolbar({
  queue,
  search,
  status,
  position,
  team,
  source,
  starred,
  addCandidate,
  primaryAction: primaryActionProp,
  showClearFilters,
  onClearFilters,
  clearFiltersLabel,
}: Props) {
  const primaryAction = primaryActionProp ?? addCandidate;
  const PrimaryIcon = primaryAction?.icon ?? UserPlus;
  const showClear = showClearFilters && onClearFilters && clearFiltersLabel;

  // Filter cells for the mobile 2-col grid (no primary action — it lives in the search row)
  const mobileCells: ReactNode[] = [
    <StatusSelect key="status" filter={status} />,
  ];
  if (position) {
    mobileCells.push(
      <GenericSelect key="position" filter={position} widthClass="md:w-[180px]" />,
    );
  }
  if (source) {
    mobileCells.push(
      <GenericSelect key="source" filter={source} widthClass="md:w-[180px]" />,
    );
  }
  if (team) {
    mobileCells.push(
      <GenericSelect key="team" filter={team} widthClass="md:w-[160px]" />,
    );
  }
  if (starred) {
    mobileCells.push(
      <GenericSelect key="starred" filter={starred} widthClass="md:w-[160px]" />,
    );
  }
  if (showClear) {
    mobileCells.push(
      <div key="clear" className="flex h-9 items-center justify-center">
        <ClearListFiltersButton onClick={onClearFilters} label={clearFiltersLabel} />
      </div>,
    );
  }

  return (
    <div className="shrink-0 space-y-3">
      {/* ── Mobile layout (hidden on md+) ── */}
      <div className="flex flex-col gap-3 md:hidden">

        {/* Row 1: search + primary action button side by side */}
        <div className="flex items-center gap-2">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 z-10 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={search.placeholder}
              value={search.value}
              onChange={(e) => search.onChange(e.target.value)}
              className={activeListFilterClass(search.value.trim().length > 0, 'h-9 w-full pl-9')}
            />
          </div>
          {primaryAction ? (
            <Button
              type="button"
              size="sm"
              disabled={primaryAction.disabled}
              onClick={primaryAction.onClick}
              className="h-9 shrink-0 gap-1.5 px-3"
            >
              <PrimaryIcon className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{primaryAction.label}</span>
            </Button>
          ) : null}
        </div>

        {/* Row 2: queue toggle (optional) */}
        {queue ? (
          <ApplicationQueueToggle
            {...queue}
            className="flex w-full [&>button]:flex-1 [&>button]:justify-center"
          />
        ) : null}

        {/* Row 3: filter grid */}
        {mobileCells.length > 0 ? (
          <div className="grid grid-cols-2 gap-2">
            {mobileCells.map((cell, index) => (
              <FilterCell
                key={index}
                className={
                  index === mobileCells.length - 1 && mobileCells.length % 2 === 1
                    ? 'col-span-2'
                    : undefined
                }
              >
                {cell}
              </FilterCell>
            ))}
          </div>
        ) : null}
      </div>

      {/* ── Desktop layout (hidden below md) ── */}
      <div className="hidden min-w-0 flex-wrap items-center gap-3 md:flex">
        {queue ? <ApplicationQueueToggle {...queue} /> : null}
        <div className="relative min-w-[160px] max-w-xs flex-1 basis-[200px]">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 z-10 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={search.placeholder}
            value={search.value}
            onChange={(e) => search.onChange(e.target.value)}
            className={activeListFilterClass(search.value.trim().length > 0, 'h-9 w-full pl-9')}
          />
        </div>
        <StatusSelect filter={status} />
        {position ? <GenericSelect filter={position} widthClass="w-[180px]" /> : null}
        {source ? <GenericSelect filter={source} widthClass="w-[180px]" /> : null}
        {team ? <GenericSelect filter={team} widthClass="w-[160px]" /> : null}
        {starred ? <GenericSelect filter={starred} widthClass="w-[160px]" /> : null}
        {showClear ? (
          <ClearListFiltersButton onClick={onClearFilters} label={clearFiltersLabel} />
        ) : null}
        {primaryAction ? (
          <Button
            type="button"
            size="sm"
            disabled={primaryAction.disabled}
            onClick={primaryAction.onClick}
            className="h-9 shrink-0 gap-1.5"
          >
            <PrimaryIcon className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{primaryAction.label}</span>
          </Button>
        ) : null}
      </div>
    </div>
  );
}
